import { useState, useCallback, useRef } from "react";
import { hexEncodeUtf8 } from "./useXrpWallet";

const PROJECT_ID = process.env.REACT_APP_WC_PROJECT_ID || "718268298da12ec7ba635470cce466bc";
const CHAIN_ID   = "xrpl:0"; // XRPL mainnet


// Extract r-address from WalletConnect account string "xrpl:0:rAddress..."
function parseXrplAddress(accounts = []) {
  const xrplAcc = accounts.find((a) => a.startsWith("xrpl:"));
  return xrplAcc?.split(":")?.[2] || null;
}

// Singletons — initialised once per page load
let _provider = null;
let _modal    = null;

async function init() {
  if (_provider) return { provider: _provider, modal: _modal };

  const [{ UniversalProvider }, { utils }] = await Promise.all([
    import("@walletconnect/universal-provider"),
    import("@joey-wallet/wc-client"),
  ]);

  _provider = await UniversalProvider.init({
    projectId: PROJECT_ID,
    metadata: {
      name:        "BESC Bridge",
      description: "BESC Cross-Chain Bridge",
      url:         "https://wbescbridge.com",
      icons:       ["https://wbescbridge.com/favicon.ico"],
    },
  });

  // Create the Reown AppKit modal — features Joey Wallet + any other XRPL WC wallet
  const { data: result } = utils.connect.generateModal({
    projectId: PROJECT_ID,
    provider:  _provider,
    chain:     CHAIN_ID,
  });
  _modal = result?.modal ?? null;

  return { provider: _provider, modal: _modal };
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

export const useWalletConnectXrp = ({ enabled = false } = {}) => {
  const [connected, setConnected]   = useState(false);
  const [address, setAddress]       = useState("");
  const sessionRef                  = useRef(null);

  const connect = useCallback(async () => {
    const { provider, modal } = await init();
    const { utils } = await import("@joey-wallet/wc-client");

    // If a session already exists (page reload), restore it immediately
    const existingSession = provider.session;
    if (existingSession) {
      const accounts = Object.values(existingSession.namespaces || {}).flatMap((ns) => ns.accounts || []);
      const addr = parseXrplAddress(accounts);
      if (addr) {
        sessionRef.current = existingSession;
        setAddress(addr);
        setConnected(true);
        return addr;
      }
    }

    // Start WalletConnect pairing in background — use Joey SDK's default xrplNamespace (correctly formatted)
    utils.connect.handleConnection({ provider, chain: CHAIN_ID });

    // Open the AppKit modal — user picks any XRPL WalletConnect wallet
    if (modal) {
      modal.open().catch(() => {});
    }

    // Wait for the user to approve in their wallet
    return new Promise((resolve, reject) => {
      const timeout = setTimeout(
        () => reject(new Error("WalletConnect connection timed out.")),
        5 * 60 * 1000
      );

      const finish = (accounts) => {
        clearTimeout(timeout);
        const addr = parseXrplAddress(accounts);
        if (!addr) return reject(new Error("No XRP address returned from wallet."));
        setAddress(addr);
        setConnected(true);
        resolve(addr);
      };

      provider.once("connect", (session) => {
        sessionRef.current = session;
        const accounts = Object.values(session?.namespaces || {}).flatMap((ns) => ns.accounts || []);
        finish(accounts);
      });

      provider.once("session_update", ({ session }) => {
        if (session) {
          sessionRef.current = session;
          const accounts = Object.values(session.namespaces || {}).flatMap((ns) => ns.accounts || []);
          finish(accounts);
        }
      });
    });
  }, []);

  const disconnect = useCallback(async () => {
    try {
      if (_provider?.session) await _provider.disconnect();
    } catch { /* no-op */ }
    _provider = null;
    _modal    = null;
    sessionRef.current = null;
    setConnected(false);
    setAddress("");
  }, []);

  const send = useCallback(async ({ drops, destination, bescAddress }) => {
    if (!connected || !address) throw new Error("WalletConnect XRP wallet is not connected.");

    const { provider } = await init();

    // provider.request directly maps to xrpl_signTransaction on the connected wallet
    const response = await provider.request(
      {
        method: "xrpl_signTransaction",
        params: {
          tx_json: {
            TransactionType: "Payment",
            Account:     address,
            Destination: destination,
            Amount:      drops.toString(), // drops
            Memos: [{
              Memo: {
                MemoData: hexEncodeUtf8(bescAddress),
                MemoType: hexEncodeUtf8("besc-dest"),
              },
            }],
          },
          options: { autofill: true, submit: true },
        },
      },
      CHAIN_ID
    );

    // Response shape: { tx_json: { hash: "...", ... } }
    const hash = response?.tx_json?.hash ?? response?.hash ?? response?.result?.hash;
    if (!hash) throw new Error("WalletConnect wallet did not return a transaction hash.");
    return hash.toLowerCase();
  }, [connected, address]);

  return {
    available: enabled && !!PROJECT_ID,
    connected,
    address,
    connect,
    disconnect,
    send,
  };
};
